import { useState } from "react";
import { normalizeTypedAnswer } from "@hootapp/shared";
import { api, ApiError } from "../../lib/api.js";
import type { QuestionType, QuizDraft } from "./types.js";
import {
  DEFAULT_TIMER_SECONDS,
  MAX_OPTIONS,
  MAX_TIMER_SECONDS,
  MIN_OPTIONS,
  MIN_TIMER_SECONDS,
} from "./types.js";
import {
  errorText,
  ghostButton,
  primaryButton,
  surface,
  textInput,
} from "./quiz-ui.js";
import { useT, type Translate } from "../../i18n/index.js";

export interface QuestionFormInitialValues {
  type: QuestionType;
  prompt: string;
  timerSeconds: number;
  options: { label: string; isCorrect: boolean }[];
  typedAnswers: string;
}

interface QuestionFormProps {
  onSubmit: (draft: QuizDraft) => Promise<void>;
  onCancel: () => void;
  initialValues?: QuestionFormInitialValues;
  existingImageAssetId?: string | null | undefined;
}

interface OptionDraft {
  label: string;
  isCorrect: boolean;
}

const QUESTION_TYPES: QuestionType[] = ["quiz_single", "quiz_multi", "true_false", "type_answer", "slide"];

function makeBlankOptions(): OptionDraft[] {
  return [
    { label: "", isCorrect: true },
    { label: "", isCorrect: false },
  ];
}

function typeUsesOptions(type: QuestionType): boolean {
  return type === "quiz_single" || type === "quiz_multi" || type === "true_false";
}

/**
 * Split the editor's free-text field into accepted answers. The same
 * normalization the scoring engine applies to a player's submission is used
 * here so the editor preview matches what will actually be accepted; the
 * server re-applies it regardless.
 */
function normalizeTypedAnswers(raw: string): { normalizedValue: string }[] {
  const seen = new Set<string>();
  return raw
    .split(/\r?\n|,/)
    .map(normalizeTypedAnswer)
    .filter((answer) => {
      if (!answer || seen.has(answer)) return false;
      seen.add(answer);
      return true;
    })
    .map((normalizedValue) => ({ normalizedValue }));
}

function validate(
  type: QuestionType,
  prompt: string,
  timerSeconds: number,
  options: OptionDraft[],
  typedAnswers: string,
  t: Translate,
): string | null {
  if (prompt.trim().length === 0) {
    return type === "slide" ? t("quiz.enterSlideText") : t("quiz.enterPrompt");
  }
  if (
    type !== "slide" &&
    (!Number.isFinite(timerSeconds) ||
      timerSeconds < MIN_TIMER_SECONDS ||
      timerSeconds > MAX_TIMER_SECONDS)
  ) {
    return t("quiz.timerRange", { min: MIN_TIMER_SECONDS, max: MAX_TIMER_SECONDS });
  }

  if (typeUsesOptions(type)) {
    if (options.some((o) => o.label.trim().length === 0)) {
      return t("quiz.optionNeedsLabel");
    }
    const correctCount = options.filter((o) => o.isCorrect).length;
    if (type === "quiz_multi") {
      if (correctCount < 1) return t("quiz.markOneCorrect");
    } else if (correctCount !== 1) {
      return t("quiz.markExactlyOne");
    }
  }

  if (type === "type_answer" && normalizeTypedAnswers(typedAnswers).length === 0) {
    return t("quiz.addAcceptedAnswer");
  }

  return null;
}

export function QuestionForm({ onSubmit, onCancel, initialValues, existingImageAssetId }: QuestionFormProps) {
  const t = useT();
  const isEditing = initialValues !== undefined;
  const [type, setType] = useState<QuestionType>(() => initialValues?.type ?? "quiz_single");
  const [prompt, setPrompt] = useState(() => initialValues?.prompt ?? "");
  const [timerSeconds, setTimerSeconds] = useState(() => initialValues?.timerSeconds ?? DEFAULT_TIMER_SECONDS);
  const [options, setOptions] = useState<OptionDraft[]>(() =>
    initialValues?.options ?? makeBlankOptions(),
  );
  const [typedAnswers, setTypedAnswers] = useState(() => initialValues?.typedAnswers ?? "");
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  const isTrueFalse = type === "true_false";
  const isMulti = type === "quiz_multi";
  const usesOptions = typeUsesOptions(type);
  const usesTypedAnswers = type === "type_answer";
  const isSlide = type === "slide";

  const handleTypeChange = (next: QuestionType) => {
    setType(next);
    setError(null);
    setTypedAnswers("");
    setOptions(next === "true_false"
      ? [{ label: t("quiz.true"), isCorrect: true }, { label: t("quiz.false"), isCorrect: false }]
      : makeBlankOptions());
  };

  const updateOptionLabel = (index: number, label: string) => {
    setOptions((prev) =>
      prev.map((opt, i) => (i === index ? { ...opt, label } : opt)),
    );
  };

  const markCorrect = (index: number) => {
    setOptions((prev) =>
      prev.map((opt, i) =>
        isMulti
          ? i === index
            ? { ...opt, isCorrect: !opt.isCorrect }
            : opt
          : { ...opt, isCorrect: i === index },
      ),
    );
  };

  const addOption = () => {
    setOptions((prev) =>
      prev.length >= MAX_OPTIONS
        ? prev
        : [...prev, { label: "", isCorrect: false }],
    );
  };

  const removeOption = (index: number) => {
    setOptions((prev) => {
      if (prev.length <= MIN_OPTIONS) return prev;
      const next = prev.filter((_, i) => i !== index);
      if (!next.some((o) => o.isCorrect) && next.length > 0) {
        return next.map((o, i) => ({ ...o, isCorrect: i === 0 }));
      }
      return next;
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const validationError = validate(type, prompt, timerSeconds, options, typedAnswers, t);
    if (validationError) {
      setError(validationError);
      return;
    }

    const acceptedAnswers = normalizeTypedAnswers(typedAnswers);
    setError(null);
    setIsSaving(true);
    try {
      let imageAssetId: string | undefined;
      if (imageFile) {
        const formData = new FormData();
        formData.append("file", imageFile);
        const uploaded = await api.upload<{ assetId: string; url: string }>(
          "/media/upload",
          formData,
        );
        imageAssetId = uploaded.assetId;
      } else if (existingImageAssetId) {
        imageAssetId = existingImageAssetId;
      }
      await onSubmit({
        type,
        prompt: prompt.trim(),
        timerSeconds,
        options: usesOptions
          ? options.map((o) => ({
              label: o.label.trim(),
              isCorrect: o.isCorrect,
            }))
          : [],
        ...(usesTypedAnswers ? { acceptedAnswers } : {}),
        ...(imageAssetId ? { imageAssetId } : {}),
      });
    } catch (err) {
      setError(
        err instanceof ApiError || err instanceof Error
          ? err.message
          : t("quiz.saveQuestionFailed"),
      );
      setIsSaving(false);
    }
  };

  return (
    <form
      onSubmit={handleSubmit}
      aria-label={isEditing ? t("quiz.editQuestion") : t("quiz.newQuestion")}
      className="question-form"
      style={surface}
    >
      <h3 className="question-form-heading">{isEditing ? t("quiz.editQuestion") : t("quiz.addQuestionHeading")}</h3>

      {error && (
        <p role="alert" style={errorText}>
          {error}
        </p>
      )}

      <label className="question-form-field">
        {t("quiz.questionType")}
        <select
          value={type}
          onChange={(e) => handleTypeChange(e.target.value as QuestionType)}
          className="question-form-control"
          style={{ ...textInput, cursor: "pointer" }}
        >
          {QUESTION_TYPES.map((value) => (
            <option key={value} value={value}>
              {({
                quiz_single: t("quiz.typeSingle"),
                quiz_multi: t("quiz.typeMultiple"),
                true_false: t("quiz.typeTrueFalse"),
                type_answer: t("quiz.typeAnswer"),
                slide: t("quiz.typeSlide"),
              } as Record<QuestionType, string>)[value]}
            </option>
          ))}
        </select>
      </label>

      <label className="question-form-field">
        {isSlide ? t("quiz.slideText") : t("quiz.prompt")}
        <textarea
          value={prompt}
          onChange={(e) => setPrompt(e.target.value)}
          required
          rows={isSlide ? 4 : 2}
          placeholder={isSlide ? t("quiz.slidePlaceholder") : t("quiz.promptPlaceholder")}
          className="question-form-control"
          style={{ ...textInput, resize: "vertical", minHeight: isSlide ? "6rem" : "3rem" }}
        />
      </label>

      {!isSlide && (
        <label className="question-form-field question-form-timer-field">
          {t("quiz.timerSeconds")}
          <input
            type="number"
            value={timerSeconds}
            min={MIN_TIMER_SECONDS}
            max={MAX_TIMER_SECONDS}
            onChange={(e) => setTimerSeconds(Number(e.target.value))}
            className="question-form-control"
            style={textInput}
          />
        </label>
      )}

      <label className="question-form-field">
        {t("quiz.image")}
        <input
          type="file"
          accept="image/png,image/jpeg,image/gif,image/webp,image/avif"
          onChange={(e) => setImageFile(e.target.files?.[0] ?? null)}
          className="question-form-control question-form-file-input"
          style={textInput}
        />
      </label>

      {usesOptions && (
        <fieldset
          className="question-form-options"
        >
          <legend className="question-form-legend">
            {t("quiz.answerOptions")}
          </legend>
          <p className="question-form-help">
            {isMulti
              ? t("quiz.chooseEveryCorrect")
              : t("quiz.chooseOneCorrect")}
          </p>
          {options.map((option, index) => (
            <div
              key={index}
              className="question-form-option-row"
            >
              <label className="question-form-correct-toggle">
                <input
                  type={isMulti ? "checkbox" : "radio"}
                  name={isMulti ? `correct-option-${index}` : "correct-option"}
                  checked={option.isCorrect}
                  onChange={() => markCorrect(index)}
                  aria-label={t("quiz.markCorrect", { number: index + 1 })}
                />
              </label>
              <input
                type="text"
                value={option.label}
                onChange={(e) => updateOptionLabel(index, e.target.value)}
                readOnly={isTrueFalse}
                aria-label={t("quiz.optionLabel", { number: index + 1 })}
                placeholder={t("quiz.optionPlaceholder", { number: index + 1 })}
                className="question-form-control question-form-option-input"
                style={{
                  ...textInput,
                  opacity: isTrueFalse ? 0.7 : 1,
                }}
              />
              {!isTrueFalse && options.length > MIN_OPTIONS && (
                <button
                  type="button"
                  onClick={() => removeOption(index)}
                  aria-label={t("quiz.removeOption", { number: index + 1 })}
                  className="question-form-remove-button"
                  style={{
                    ...ghostButton,
                  }}
                >
                  {t("quiz.remove")}
                </button>
              )}
            </div>
          ))}
          {!isTrueFalse && options.length < MAX_OPTIONS && (
            <button
              type="button"
              onClick={addOption}
              className="question-form-add-option"
              style={{
                ...ghostButton,
              }}
            >
              {t("quiz.addOption")}
            </button>
          )}
        </fieldset>
      )}

      {usesTypedAnswers && (
        <label className="question-form-field">
          {t("quiz.acceptedAnswers")}
          <textarea
            value={typedAnswers}
            onChange={(e) => setTypedAnswers(e.target.value)}
            rows={4}
            placeholder={t("quiz.acceptedAnswersPlaceholder")}
            className="question-form-control"
            style={{ ...textInput, resize: "vertical", minHeight: "5rem" }}
          />
        </label>
      )}

      <div className="question-form-actions">
        <button
          type="submit"
          className="btn-tactile"
          disabled={isSaving}
          style={{ ...primaryButton, opacity: isSaving ? 0.7 : 1 }}
        >
          {isSaving ? t("quiz.saving") : isEditing ? t("quiz.saveChanges") : t("quiz.saveQuestion")}
        </button>
        <button
          type="button"
          onClick={onCancel}
          disabled={isSaving}
          style={ghostButton}
        >
          {t("common.cancel")}
        </button>
      </div>
    </form>
  );
}
